import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Grid, Typography, useTheme } from '@mui/material';
import { useState, type FC, type PropsWithChildren, useMemo } from 'react';
import { useLoaderData } from 'react-router';
import { useTimeout } from 'react-use';

import { virtualCardTypes } from '../../../../constants/appConstants';
import type { VirtualCardDataType } from '../../../../repositories/paymentRepository';
import type { WalletDetailsApiResponseType } from '../../../../repositories/walletRepository';
import type { walletDetailsLoader } from '../../../../router/loaders/walletDetails';
import { setTime } from '../../../../utils/setTime';
import { getCancelledWord } from '../../../../utils/translationUtil';
import { getFullName } from '../../request/newRequest/requestDetail';
import {
	ExpirationLabel,
	ExpirationValue,
	ProviderImage,
	StyledBox,
	StyledCard,
	StyledChip,
	StyledElement,
	StyledGrid,
	StyledHoverText,
	formatCardNumber,
	getCVVNumber,
	getCardNumber,
	getFormattedExpiration,
} from '../../request/virtualCard';
import BouncingLoaderComp from '../component/bouncingLoaderComp';
import TruncatedRequesterFullNameText from '../component/truncatedRequesterFullNameText';

type WalletCardProps = {
	task: WalletDetailsApiResponseType & { card: VirtualCardDataType | undefined };
};

function isExpired(card) {
	return card?.daysRemaining < 0;
}

function isCanceled(card) {
	return card?.status === 'CANCELED' ||
		card?.status === 'UNAVAILABLE' ||
		card?.status === 'CREATION_ERROR' ||
		card?.status === 'UNKNOWN'
		? true
		: !isExpired(card) && card?.status === 'EXPIRED';
}

function isActive(card) {
	return !isExpired(card) && !isCanceled(card);
}

export const getVirtualCardTypeLabel = ({
	card,
	fields,
}: {
	card: VirtualCardDataType | undefined;
	fields?: any;
}): string => {
	if (!card) {
		return '';
	}
	const cardTypeLabel = virtualCardTypes.find(({ cardsKey }) => cardsKey === card?.cardType)?.label || '';

	if (card?.cardType === 'RECURRING_USE') {
		const frequency = card.frequency || fields?.frequency;
		if (!frequency) {
			return '';
		}
		const frequencyLabel = frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase() : '';
		return frequencyLabel + ' ' + cardTypeLabel;
	} else {
		return cardTypeLabel || '';
	}
};

function getStatusLabel(card) {
	if (isExpired(card)) {
		return 'Expired';
	} else if (isCanceled(card)) {
		return getCancelledWord('Canceled');
	} else {
		return 'Active';
	}
}

function getVirtualCardStatusAndLabel({ card, fields }) {
	const statusLabel = isExpired(card) || isCanceled(card) ? `${getStatusLabel(card)} - ` : '';
	return statusLabel + getVirtualCardTypeLabel({ card, fields });
}

function mapCardStatusToMessage(status: string | undefined | null) {
	switch (status) {
		case 'IN_PROGRESS': {
			return {
				message: 'Card is not active yet. All card details will be available once the card is active.',
				type: 'warning' as const,
			};
		}
		case null:
		case undefined:
		case 'UNKNOWN':
		case 'UNAVAILABLE': {
			return {
				message: 'An error occurred while retrieving card details. If the problem persists, please contact support.',
				type: 'error' as const,
			};
		}
		case 'CREATION_ERROR': {
			return {
				message: "Card creation failed on the provider's side.",
				type: 'error' as const,
			};
		}
		default: {
			return null;
		}
	}
}

export const expirationLabel = 'EXP DATE';
export const Cvc = 'cvc';

const WalletCard: FC<PropsWithChildren<WalletCardProps>> = ({ task, children }) => {
	const initialData = useLoaderData() as Awaited<ReturnType<typeof walletDetailsLoader>>;
	const [isHovering, setIsHovering] = useState(false);
	const [copyDetails, setCopyDetails] = useState({ isCopied: false, copySuccessMessage: '' });
	const fields = task?.context?.request?.fields;

	const getDisplayCard = () => {
		if (!task?.card || isActive(task.card)) {
			return task?.card;
		}
		return { ...task.card, number: null, cvv: null, expirationDate: null };
	};
	const displayCard = getDisplayCard();
	const cardNumber = getCardNumber(displayCard);
	const requesterFullName = useMemo(() => getFullName(task?.request?.requester || {}), [task?.request?.requester]);
	const isCardOwner = task?.assignees?.includes(initialData?.userData?.userId);
	const isCardActiveAndOwner = isCardOwner && isActive(task?.card);
	const isCopyAllowed = !Number.isNaN(Number(cardNumber)) && isCardActiveAndOwner;
	const theme = useTheme();
	const [isReady] = useTimeout(setTime(5, 'seconds'));
	const errorNotification = isReady() || task?.card?.status ? mapCardStatusToMessage(task?.card?.status) : '';
	const copyItems = {
		number: {
			name: 'card number',
			data: cardNumber,
		},
		cvc: {
			name: Cvc,
			data: getCVVNumber(displayCard),
		},
	};
	const virtualCardLabelAndStatus = getVirtualCardStatusAndLabel({ card: task?.card, fields });
	const formattedExpirationDate = getFormattedExpiration(displayCard?.expirationDate);
	const setCopyItem = (copyItemKey, event) => {
		event.stopPropagation();
		if (!isCardActiveAndOwner || !copyItems[copyItemKey] || !isCopyAllowed) {
			return;
		}
		const copyItem = copyItems[copyItemKey];
		setIsHovering(true);
		setCopyDetails({
			isCopied: false,
			copySuccessMessage: 'Copied ' + copyItem?.name + '!',
		});
	};

	async function onCopySuccess(copyItemKey, event) {
		event.stopPropagation();
		if (!isCardActiveAndOwner || !copyItems[copyItemKey] || !isCopyAllowed) {
			return;
		}
		const copyItem = copyItems[copyItemKey];
		await navigator.clipboard.writeText(copyItem.data);
		setCopyDetails({
			isCopied: true,
			copySuccessMessage: 'Copied ' + copyItem?.name + '!',
		});
	}

	return (
		<>
			{errorNotification && isCardOwner && (
				<Alert
					sx={{ width: '100%', marginBottom: theme.tsGoUnit.tsUnitHalf }}
					data-testid="card-status-alert"
					severity={errorNotification.type}
				>
					{errorNotification.message}
				</Alert>
			)}
			<StyledCard
				onMouseOver={event => setCopyItem('number', event)}
				onMouseOut={() => setIsHovering(false)}
				onClick={event => onCopySuccess('number', event)}
				sx={[
					{
						cursor: isCopyAllowed && isHovering ? 'pointer' : 'default',
						width: '290px',
					},
				]}
				data-testid="wallet-card"
			>
				{virtualCardLabelAndStatus ? (
					isReady() || task?.card?.status ? (
						<StyledChip label={virtualCardLabelAndStatus} color={isActive(task?.card) ? 'success' : 'error'} />
					) : (
						<BouncingLoaderComp />
					)
				) : null}
				<StyledHoverText>
					{isCopyAllowed && isHovering ? (copyDetails.isCopied ? copyDetails.copySuccessMessage : '') : ''}
				</StyledHoverText>
				<StyledGrid className={isCopyAllowed ? 'isRevealDetailsAllowed' : ''}>
					{formatCardNumber(task?.card).map((cardNumberPiece, index, segments) => {
						const isLastSegment = index === segments.length - 1;
						const isBlurred = isCardActiveAndOwner && !isHovering && !isLastSegment;
						const displayValue = isCardActiveAndOwner || isLastSegment ? cardNumberPiece : '••••';
						return (
							<Typography
								key={`${cardNumberPiece} ${index}`}
								sx={[
									{ fontSize: '22px', color: 'hsl(0, 0%, 100%) !important' },
									isBlurred ? { filter: 'blur(8px) !important', userSelect: 'none' } : {},
								]}
							>
								{displayValue}
							</Typography>
						);
					})}
					{isCopyAllowed && (
						<ContentCopyIcon
							sx={{
								fontSize: theme.typography.pxToRem(14),
								color: 'hsl(0, 0%, 100%)',
								marginLeft: theme.tsGoUnit.tsUnitQuarter,
							}}
						/>
					)}
				</StyledGrid>
				<StyledBox>
					{formattedExpirationDate && isCardActiveAndOwner ? (
						<StyledElement>
							{expirationLabel ? <ExpirationLabel>{expirationLabel}</ExpirationLabel> : null}
							<ExpirationValue data-testid="expirationDate">{formattedExpirationDate}</ExpirationValue>
						</StyledElement>
					) : null}
					{isCardActiveAndOwner ? (
						<StyledElement
							onMouseOver={event => setCopyItem('cvc', event)}
							onMouseOut={() => setIsHovering(false)}
							onClick={event => onCopySuccess('cvc', event)}
							sx={{ display: 'flex', justifyContent: 'flex-end' }}
						>
							<ExpirationLabel>{Cvc}</ExpirationLabel>
							<ExpirationValue data-testid="cvvValue">{getCVVNumber(displayCard)}</ExpirationValue>
							{isCopyAllowed && (
								<ContentCopyIcon sx={{ fontSize: theme.typography.pxToRem(14), color: 'hsl(0, 0%, 100%)' }} />
							)}
						</StyledElement>
					) : null}
				</StyledBox>
				<Grid
					container
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'baseline',
					}}
				>
					{requesterFullName ? <TruncatedRequesterFullNameText text={requesterFullName} fontSize={'12px'} /> : null}

					<Grid size={2}>
						<ProviderImage provider={task?.card?.provider || 'N/A'} />
					</Grid>
				</Grid>
				{children}
			</StyledCard>
		</>
	);
};

export default WalletCard;
